// src/features/events/components/EventMetadataPopover.tsx

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Popover,
  Typography,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Code as CodeIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify } from 'react-admin';

interface EventMetadataPopoverProps {
  metadata: Record<string, string> | null | undefined;
  eventCode?: string;
  errorCode?: string | null;
  /** Show all metadata inline instead of a count chip */
  expanded?: boolean;
  /** Keys to exclude from inline display (already shown in dedicated columns) */
  excludeKeys?: string[];
}

/**
 * Metadata display component with two modes:
 * - Compact (default): 2-3 inline chips + "+N" that opens popover
 * - Expanded: all key:value chips inline
 */
export const EventMetadataPopover: React.FC<EventMetadataPopoverProps> = ({
  metadata,
  eventCode,
  errorCode,
  expanded = false,
  excludeKeys = [],
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const allEntries = metadata ? Object.entries(metadata) : [];
  // Filter out keys that are already shown in dedicated columns
  const entries = allEntries.filter(([key]) => !excludeKeys.includes(key));
  const hasData = entries.length > 0 || errorCode;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyJSON = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const data = { eventCode, errorCode, metadata };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    notify('Metadata copied to clipboard', { type: 'info' });
  };

  const open = Boolean(anchorEl);

  // If no data, show dash
  if (!hasData) {
    return <Typography component="span" color="text.disabled">-</Typography>;
  }

  const chipSx = {
    height: 20,
    fontSize: '0.65rem',
    fontFamily: 'monospace',
    maxWidth: 200,
    '& .MuiChip-label': { px: 0.75 },
  };

  // Expanded mode: show all entries as inline chips
  if (expanded) {
    return (
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 420, alignItems: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        {entries.map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${value}`}
            size="small"
            variant="outlined"
            sx={chipSx}
          />
        ))}
        {entries.length > 0 && (
          <IconButton
            size="small"
            onClick={handleCopyJSON}
            title={translate('resources.events.metadata.copyJson')}
            sx={{ width: 18, height: 18, opacity: 0.4, '&:hover': { opacity: 1 } }}
          >
            <CopyIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>
    );
  }

  // Compact mode: show first 2 chips + overflow trigger
  const maxVisible = 2;
  const visible = entries.slice(0, maxVisible);
  const remaining = entries.length - maxVisible;

  return (
    <>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 320, alignItems: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        {visible.map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${value}`}
            size="small"
            variant="outlined"
            sx={chipSx}
          />
        ))}
        {remaining > 0 && (
          <Chip
            label={`+${remaining}`}
            size="small"
            onClick={handleClick}
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              cursor: 'pointer',
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.2) },
            }}
          />
        )}
        {remaining <= 0 && entries.length > 0 && (
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{ width: 18, height: 18, opacity: 0.4, '&:hover': { opacity: 1 } }}
          >
            <CodeIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>

      {/* Popover with full metadata */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { p: 2, maxWidth: 500, maxHeight: 400, overflow: 'auto' },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {translate('resources.events.metadata.title')}
            </Typography>
            <Chip
              label={allEntries.length}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={handleCopyJSON} title="Copy JSON">
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Event/Error Info */}
        {(eventCode || errorCode) && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {eventCode && (
              <Chip
                label={eventCode}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', fontFamily: 'monospace' }}
              />
            )}
            {errorCode && (
              <Chip
                label={`Error: ${errorCode}`}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        )}

        {/* All metadata (including excluded keys, since this is the full view) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          {allEntries.map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1.5,
                backgroundColor: alpha(theme.palette.grey[500], 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  borderColor: alpha(theme.palette.info.main, 0.3),
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.info.main,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {key} : {value || '-'}
              </Typography>
            </Box>
          ))}
        </Box>

        {allEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate('resources.events.metadata.empty')}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default EventMetadataPopover;
